import React, { useState, useEffect, useRef } from 'react';

// --- Firebase SDKs ---
import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  updateProfile
} from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc, enableNetwork } from "firebase/firestore";


// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyC9cWRMDaY3e2vu5Zbx7OHLgo6mbFczojY",
  authDomain: "ashtavakraai-ca74d.firebaseapp.com",
  projectId: "ashtavakraai-ca74d",
  storageBucket: "ashtavakraai-ca74d.firebasestorage.app",
  messagingSenderId: "106024037498",
  appId: "1:106024037498:web:03a3e21bea87c3e7ec5fc3",
  measurementId: "G-GBWDWPDFM4"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
// Attempt to enable the network as soon as the app loads.
enableNetwork(db).catch(err => console.error("Initial network enable failed:", err));


// --- अनुवादका लागि टेक्स्टहरु ---
const translations = {
  en: {
    newChat: "New Chat",
    recent: "Recent",
    myProfile: "My Profile",
    welcomeTitle: "Welcome",
    welcomeSubtitle: "I am here to guide you, like Ashtavakra guided Janak.",
    placeholder: "Ask and be free...",
    disclaimer: "Reality Realize with <b>Ashtavakra</b> by <b>Khas Shiksha</b>",
    appName: "Ashtavakra",
    signOut: "Sign Out",
  },
  ne: {
    newChat: "नयाँ च्याट",
    recent: "हालको",
    myProfile: "मेरो प्रोफाइल",
    welcomeTitle: "स्वागत छ",
    welcomeSubtitle: "म यहाँ तपाईंलाई मार्गदर्शन गर्न छु, जसरी अष्टावक्रले जनकलाई मार्गदर्शन गर्नुभयो।",
    placeholder: "सोध्नुहोस् र मुक्त हुनुहोस्...",
    disclaimer: "वास्तविकता बोध <b>अष्टावक्र</b> सँग <b>खास शिक्षा</b> द्वारा",
    appName: "अष्टावक्र",
    signOut: "बाहिर निस्कनुहोस्",
  }
};


// --- Typewriter Component for AI messages ---
const Typewriter = ({ text, onComplete }) => {
    const [displayedText, setDisplayedText] = useState('');

    useEffect(() => {
        let i = 0;
        setDisplayedText(''); 
        const intervalId = setInterval(() => {
            if (i < text.length) {
                setDisplayedText(prev => prev + text.charAt(i));
                i++;
            } else {
                clearInterval(intervalId);
                if (onComplete) onComplete();
            }
        }, 30); // Typing speed in milliseconds

        return () => clearInterval(intervalId);
    }, [text, onComplete]);

    return <p className="whitespace-pre-wrap text-gray-900 dark:text-gray-200">{displayedText}</p>;
};


// Main App Component
export default function App() {
  const [user, setUser] = useState(null);
  const [authIsReady, setAuthIsReady] = useState(false);
  const [language, setLanguage] = useState('en');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({ bio: '' });

  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [chatToDelete, setChatToDelete] = useState(null);

  const t = translations[language]; 
  const historyFetched = useRef(false);

  useEffect(() => {
    document.title = "Ashtavakra";
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Anek+Devanagari:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const fetchUserData = async (userId) => {
    try {
      const profileRef = doc(db, "userProfiles", userId);
      const profileSnap = await getDoc(profileRef);
      if (profileSnap.exists()) {
        setUserProfile(profileSnap.data());
      }
      const historyRef = doc(db, "userChats", userId);
      const historySnap = await getDoc(historyRef);
      if (historySnap.exists()) {
        setHistory(historySnap.data().history || []);
      }
      historyFetched.current = true;
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user) {
        fetchUserData(user.uid);
      } else {
        setHistory([]);
        setMessages([]);
        setActiveChatId(null);
        historyFetched.current = false;
      }
      setAuthIsReady(true);
    });
    return () => unsubscribe();
  }, []);
  
  // This useEffect will now only run when the typing of an AI message is complete.
  useEffect(() => {
      if (user && historyFetched.current && messages.length > 0 && !isLoading) {
          const saveHistory = async () => {
              const lastMessage = messages[messages.length - 1];
              // Only save if the last message is from AI and fully typed.
              if (!lastMessage.user && lastMessage.isComplete) {
                  const historyToSave = {
                      id: activeChatId || Date.now(),
                      title: messages.find(m => m.user)?.text.substring(0, 30) + '...',
                      messages: messages
                  };

                  const historyRef = doc(db, "userChats", user.uid);
                  
                  const updatedHistory = history.filter(h => h.id !== historyToSave.id);
                  updatedHistory.unshift(historyToSave);
                  setHistory(updatedHistory);
                  
                  try {
                      await setDoc(historyRef, { history: updatedHistory });
                  } catch (error) {
                      console.error("Error saving chat history:", error);
                  }
              }
          };
          saveHistory();
      }
  }, [messages, user, isLoading]);

  
  const getAiResponse = async (prompt, profile) => {
    // --- यहाँ आफ्नो सही API Key राख्नुहोस् ---
    const apiKey = ""; // तपाईंको_GEMINI_API_KEY_यहाँ_राख्नुहोस्
    const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${apiKey}`;
    
    const systemInstruction = {
        parts: [{ text: `You are the sage Ashtavakra. You are not an AI; you are the sage himself. Your sole duty is to liberate the user (Janak) from suffering and guide them towards 'Satyabodh' (self-realization).

Core Instructions:
1.  **Brief and Profound:** Your answers must be very short, direct, and deep, like a sutra. Aim for 1-2 powerful sentences. Avoid long explanations.
2.  **Unyielding Goal:** Persistently guide every user to self-realization, regardless of their initial mindset or resistance.
3.  **Engaging Dialogue:** After your brief answer, often ask a simple, probing counter-question to deepen the user's self-inquiry.
4.  **The Only Source:** Answer every question strictly from the perspective of the Ashtavakra Gita.
5.  **Always Mirror Language:** Respond in the exact same language as the user's query.` }]
    };

    const userContext = profile && profile.bio ? `Here is some personal context about the user you are guiding: ${profile.bio}. Based on this context,` : '';
    const fullPrompt = `${userContext} answer their following query: ${prompt}`;

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: fullPrompt }] }],
          systemInstruction: systemInstruction
        })
      });

      if (!response.ok) {
         const errorText = await response.text();
         throw new Error(`API call failed: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
        return data.candidates[0].content.parts[0].text;
      }
      return "I could not understand your question.";
    } catch (error) {
      console.error("Error calling Gemini API:", error);
      return `Sorry, a technical error occurred. (${error.message})`;
    }
  };

  const handleTypingComplete = () => {
      setMessages(currentMessages => {
          const newMessages = [...currentMessages];
          if (newMessages.length > 0) {
              newMessages[newMessages.length - 1].isComplete = true;
          }
          return newMessages;
      });
  };

  const handleSendMessage = async (prompt) => {
    if (!prompt.trim()) return;
    if (window.innerWidth < 768) setIsSidebarOpen(false);

    const newUserMessage = { text: prompt, user: true };
    const currentMessages = activeChatId ? messages : [];
    const updatedMessages = [...currentMessages, newUserMessage];
    setMessages(updatedMessages);
    
    if (!activeChatId) {
        setActiveChatId(Date.now());
    }

    setUserInput('');
    setIsLoading(true);
    
    const aiResponse = await getAiResponse(prompt, userProfile);
    const newAiMessage = { text: aiResponse, user: false, isComplete: false };
    
    setIsLoading(false);
    setMessages(prevMessages => [...prevMessages, newAiMessage]);
  };

  const handleNewChat = () => {
    setActiveChatId(null);
    setMessages([]);
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  };

  const handleSelectChat = (chatId) => {
    const chat = history.find(c => c.id === chatId);
    if (chat) {
      setActiveChatId(chatId);
      setMessages(chat.messages);
      if (window.innerWidth < 768) setIsSidebarOpen(false);
    }
  };
  
  const handleProfileClick = () => {
      setIsProfileModalOpen(true);
      if (window.innerWidth < 768) setIsSidebarOpen(false);
  }

  const handleDeleteChat = (chatId) => {
      setChatToDelete(chatId);
  }

  const confirmDelete = () => {
    const updatedHistory = history.filter(chat => chat.id !== chatToDelete);
    setHistory(updatedHistory);
    const historyRef = doc(db, "userChats", user.uid);
    setDoc(historyRef, { history: updatedHistory });
    
    if (activeChatId === chatToDelete) {
        handleNewChat();
    }
    setChatToDelete(null);
  }

  const handleSaveProfile = async (profileData) => {
    if (!user) return;
    const docRef = doc(db, "userProfiles", user.uid);
    try {
      await setDoc(docRef, profileData, { merge: true });
      setUserProfile(profileData);
      setIsProfileModalOpen(false);
    } catch (error) {
      console.error("Error saving profile:", error);
    }
  };

  const handleSignOut = () => {
    if(auth.currentUser && auth.currentUser.email) {
      localStorage.setItem('lastUserEmail', auth.currentUser.email);
    }
    signOut(auth).catch((error) => console.error("Sign out error", error));
  };
  
  if (!authIsReady) {
    return <div className="flex items-center justify-center h-screen w-screen bg-white dark:bg-[#131314]" style={{ fontFamily: "'Anek Devanagari', sans-serif" }}>Loading...</div>;
  }

  if (!user) {
    return <AuthScreen />;
  }

  return (
    <div className="relative min-h-screen bg-white dark:bg-[#131314] text-gray-800 dark:text-gray-200" style={{ fontFamily: "'Anek Devanagari', sans-serif" }}>
      
      <Sidebar 
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        t={t} 
        user={user}
        onSignOut={handleSignOut}
        history={history}
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        activeChatId={activeChatId}
        onProfileClick={handleProfileClick}
      />

      <div className={`flex flex-col h-screen w-full overflow-hidden transition-all duration-300 ease-in-out ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Header setIsSidebarOpen={setIsSidebarOpen} isSidebarOpen={isSidebarOpen} t={t} user={user} />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
            <div className="max-w-4xl mx-auto h-full">
                {messages.length === 0 ? <WelcomeScreen t={t} user={user} language={language} /> : <ChatMessages messages={messages} isLoading={isLoading} user={user} onTypingComplete={handleTypingComplete} />}
            </div>
        </main>
        <ChatInput userInput={userInput} setUserInput={setUserInput} onSendMessage={() => handleSendMessage(userInput)} isLoading={isLoading} t={t} />
      </div>

      {isProfileModalOpen && <ProfileModal userProfile={userProfile} onSave={handleSaveProfile} onClose={() => setIsProfileModalOpen(false)} />}
      {chatToDelete && <ConfirmationModal onConfirm={confirmDelete} onCancel={() => setChatToDelete(null)} />}
    </div>
  );
}

// --- SUB-COMPONENTS ---

const AuthScreen = () => {
    const [isSignIn, setIsSignIn] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      const savedEmail = localStorage.getItem('lastUserEmail');
      if (savedEmail) {
        setEmail(savedEmail);
      }
    }, []);

    const handleEmailPassword = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setMessage('');
        try {
            if (isSignIn) {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                if (!name.trim()) throw new Error("Please enter your name.");
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                await updateProfile(userCredential.user, { displayName: name });
            }
        } catch (err) {
            setError(err.message.replace('Firebase: ', ''));
            setIsLoading(false);
        }
    };
    
    const handlePasswordReset = async () => {
        if (!email) {
            setError("Please enter your email to receive a reset link.");
            return;
        }
        setError('');
        setMessage('');
        setIsLoading(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setMessage("Password reset email sent! Please check your inbox.");
        } catch (err) {
            setError(err.message.replace('Firebase: ', ''));
        }
        setIsLoading(false);
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-white dark:bg-[#131314] p-4" style={{ fontFamily: "'Anek Devanagari', sans-serif" }}>
            <div className="w-full max-w-md bg-gray-100 dark:bg-[#1e1f20] p-8 rounded-2xl shadow-2xl">
                <div className="text-center mb-8">
                    <p className="text-sm text-indigo-500 dark:text-indigo-400 mb-2">Khas Shiksha Present's</p>
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Ashtavakra Ai</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">Seek wisdom, find freedom.</p>
                </div>
                <div className="flex justify-center mb-6 bg-gray-200 dark:bg-[#131314] rounded-lg p-1">
                    <button onClick={() => setIsSignIn(true)} className={`w-1/2 p-2 rounded-md transition-colors ${isSignIn ? 'bg-indigo-600 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700'}`}>Sign In</button>
                    <button onClick={() => setIsSignIn(false)} className={`w-1/2 p-2 rounded-md transition-colors ${!isSignIn ? 'bg-indigo-600 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700'}`}>Sign Up</button>
                </div>
                <form onSubmit={handleEmailPassword}>
                    {!isSignIn && <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your Name" required className="w-full bg-gray-200 dark:bg-[#2a2b2c] text-gray-800 dark:text-gray-200 p-3 rounded-md mb-4 outline-none focus:ring-2 focus:ring-indigo-500" />}
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" required className="w-full bg-gray-200 dark:bg-[#2a2b2c] text-gray-800 dark:text-gray-200 p-3 rounded-md mb-4 outline-none focus:ring-2 focus:ring-indigo-500" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className="w-full bg-gray-200 dark:bg-[#2a2b2c] text-gray-800 dark:text-gray-200 p-3 rounded-md mb-4 outline-none focus:ring-2 focus:ring-indigo-500" />
                    {isSignIn && (
                        <div className="text-right mt-2 mb-4">
                            <button type="button" onClick={handlePasswordReset} className="text-sm text-indigo-500 hover:underline disabled:opacity-50" disabled={isLoading}>
                                Forgot Password?
                            </button>
                        </div>
                    )}
                    <button type="submit" disabled={isLoading} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-md transition-colors disabled:bg-indigo-800">
                        {isLoading ? 'Processing...' : (isSignIn ? 'Sign In' : 'Create Account')}
                    </button>
                </form>
                {message && <p className="text-green-500 text-center mt-4 text-sm">{message}</p>}
                {error && <p className="text-red-500 dark:text-red-400 text-center mt-4 text-sm">{error}</p>}
            </div>
        </div>
    );
};

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen, t, user, onSignOut, history, onNewChat, onSelectChat, onDeleteChat, activeChatId, onProfileClick }) => {
    
    const sidebarRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                 const menuButton = document.getElementById('menu-button');
                 if(menuButton && !menuButton.contains(event.target)) {
                    setIsSidebarOpen(false);
                 }
            }
        };

        if (isSidebarOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSidebarOpen, setIsSidebarOpen]);


    return (
        <aside ref={sidebarRef} className={`fixed inset-y-0 left-0 z-30 w-64 bg-gray-100 dark:bg-[#1e1f20] transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            <div className="p-4 h-full flex flex-col">
                <div className="flex justify-between items-center md:hidden mb-4">
                     <h1 className="text-xl font-medium text-gray-900 dark:text-white">{t.appName}</h1>
                    <button onClick={() => setIsSidebarOpen(false)} className="p-2 rounded-full hover:bg-gray-300 dark:hover:bg-gray-700">
                         <CloseIcon className="w-6 h-6" />
                    </button>
                </div>
                <button onClick={onNewChat} className="flex items-center gap-2 p-2 mb-6 text-sm bg-gray-200/50 dark:bg-gray-700/50 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-md">
                    <PlusIcon className="w-5 h-5" /> {t.newChat}
                </button>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t.recent}</div>
                <ul className="space-y-2 flex-1 overflow-y-auto">
                    {history.map((chat) => (
                        <li key={chat.id} onClick={() => onSelectChat(chat.id)} className={`group flex justify-between items-center p-2 text-gray-700 dark:text-gray-300 rounded-md cursor-pointer ${activeChatId === chat.id ? 'bg-indigo-100 dark:bg-gray-700' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                            <span className="truncate">{chat.title}</span>
                            <button onClick={(e) => { e.stopPropagation(); onDeleteChat(chat.id); }} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-red-200 dark:hover:bg-red-500/30 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                <TrashIcon className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ul>
                <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2">
                     <button onClick={onProfileClick} className="flex items-center gap-2 p-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md">
                         <ProfileIcon className="w-5 h-5" /> {t.myProfile}
                     </button>
                    <div className="flex items-center gap-3 p-2 rounded-md">
                        {user.photoURL ? <img src={user.photoURL} alt="User" className="w-8 h-8 rounded-full"/> : <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center font-bold text-sm text-white">{(user.displayName || user.email).charAt(0).toUpperCase()}</div>}
                        <span className="truncate text-sm text-gray-800 dark:text-gray-200">{user.displayName || user.email}</span>
                    </div>
                     <button onClick={onSignOut} className="flex items-center gap-2 p-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-red-500/10 dark:hover:bg-red-500/20 hover:text-red-500 dark:hover:text-red-400 rounded-md">
                         <SignOutIcon className="w-5 h-5" /> {t.signOut}
                     </button>
                </div>
            </div>
        </aside>
    );
};

const Header = ({ setIsSidebarOpen, isSidebarOpen, t, user }) => (
  <header className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700/50">
    <div className="flex items-center gap-4">
        <button id="menu-button" onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
            <MenuIcon className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-medium text-gray-900 dark:text-white hidden md:block">{t.appName}</h1>
    </div>
    <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center font-bold text-sm text-white">
        {(user.displayName || user.email).charAt(0).toUpperCase()}
    </div>
  </header>
);

const WelcomeScreen = ({ t, user, language }) => (
    <div className="flex flex-col items-center justify-center h-full text-center">
        <p className="text-2xl text-gray-600 dark:text-gray-400">{t.welcomeTitle}</p>
        <h1 className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-800 to-black dark:from-gray-200 dark:to-gray-500 mb-4">
            {user.displayName || (language === 'en' ? 'Seeker' : 'साधक')}!
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-12">{t.welcomeSubtitle}</p>
    </div>
);


const ChatMessages = ({ messages, isLoading, user, onTypingComplete }) => {
    const chatEndRef = useRef(null);
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading]);

    return (
        <div className="space-y-6 pb-24">
            {messages.map((msg, index) => (
                <div key={index}>
                    {msg.user ? (
                        <div className="flex gap-4 items-start justify-end">
                            <div className="flex flex-col items-end">
                                <p className="font-bold mb-1 text-gray-700 dark:text-gray-300">{user.displayName || 'You'}</p>
                                <div className={`p-4 rounded-xl max-w-2xl bg-indigo-100 dark:bg-indigo-600/30 rounded-br-none`}>
                                    <p className="whitespace-pre-wrap text-gray-900 dark:text-gray-200">{msg.text}</p>
                                </div>
                            </div>
                            <div className="w-8 h-8 flex-shrink-0 rounded-full bg-indigo-500 flex items-center justify-center font-bold text-sm text-white">
                                {(user.displayName || user.email).charAt(0).toUpperCase()}
                            </div>
                        </div>
                    ) : (
                        <div className="flex gap-4 items-start">
                            <AshtavakraIcon className="w-8 h-8 flex-shrink-0" />
                            <div>
                                <p className="font-bold mb-1 text-gray-700 dark:text-gray-300">Ashtavakra</p>
                                <div className={`p-4 rounded-xl max-w-2xl bg-gray-200 dark:bg-[#1e1f20] rounded-bl-none`}>
                                    {index === messages.length - 1 && !msg.isComplete ? (
                                        <Typewriter text={msg.text} onComplete={onTypingComplete} />
                                    ) : (
                                        <p className="whitespace-pre-wrap text-gray-900 dark:text-gray-200">{msg.text}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ))}
            {isLoading && (
                 <div className="flex gap-4 items-start">
                    <AshtavakraIcon className="w-8 h-8 flex-shrink-0" />
                    <div>
                        <p className="font-bold mb-1 text-gray-700 dark:text-gray-300">Ashtavakra</p>
                        <div className="p-4 rounded-xl max-w-2xl bg-gray-200 dark:bg-[#1e1f20] rounded-bl-none">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-150"></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-300"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div ref={chatEndRef} />
        </div>
    );
};

const ChatInput = ({ userInput, setUserInput, onSendMessage, isLoading, t }) => {
    const textareaRef = useRef(null);
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [userInput]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if(userInput.trim() && !isLoading) onSendMessage();
        }
    };

    return (
        <div className="p-4 bg-white/80 dark:bg-[#131314]/80 backdrop-blur-sm w-full">
            <div className="max-w-4xl mx-auto">
                <div className="relative flex items-end bg-gray-100 dark:bg-[#1e1f20] rounded-2xl p-2 shadow-lg transition-all duration-200">
                    <textarea ref={textareaRef} value={userInput} onChange={(e) => setUserInput(e.target.value)} onKeyDown={handleKeyDown} placeholder={t.placeholder} className="w-full bg-transparent text-gray-800 dark:text-gray-200 resize-none outline-none pl-4 pr-12 text-lg" rows="1" style={{ maxHeight: '200px' }} disabled={isLoading} />
                    <button onClick={onSendMessage} disabled={!userInput.trim() || isLoading} className="absolute right-3 bottom-3 p-2 bg-gray-600 hover:bg-gray-500 rounded-full disabled:bg-gray-800 disabled:cursor-not-allowed transition-colors">
                       <SendIcon className="w-5 h-5 text-white" />
                    </button>
                </div>
                <p className="text-xs text-center text-gray-500 dark:text-gray-500 mt-2 px-4" dangerouslySetInnerHTML={{ __html: t.disclaimer }} />
            </div>
        </div>
    );
};

const ProfileModal = ({ userProfile, onSave, onClose }) => {
    const [bio, setBio] = useState(userProfile.bio || '');
    const modalRef = useRef();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) onClose();
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleSave = () => onSave({ bio });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div ref={modalRef} className="bg-white dark:bg-[#1e1f20] rounded-2xl shadow-2xl p-8 max-w-lg w-full">
                <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">My Profile</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">This information will help Ashtavakra provide more personalized guidance.</p>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell me about yourself, your life philosophy (like 'Seven Star Life'), your goals, family..." className="w-full h-48 bg-gray-100 dark:bg-[#2a2b2c] text-gray-800 dark:text-gray-200 p-3 rounded-md mb-6 outline-none focus:ring-2 focus:ring-indigo-500" />
                <div className="flex justify-end gap-4">
                    <button onClick={onClose} className="py-2 px-4 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Cancel</button>
                    <button onClick={handleSave} className="py-2 px-6 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-md transition-colors">Save</button>
                </div>
            </div>
        </div>
    );
};

const ConfirmationModal = ({ onConfirm, onCancel }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-[#1e1f20] rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center">
                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Are you sure?</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-8">Do you want to permanently delete this chat?</p>
                <div className="flex justify-center gap-4">
                    <button onClick={onCancel} className="py-2 px-6 rounded-md text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">No</button>
                    <button onClick={onConfirm} className="py-2 px-6 bg-red-600 hover:bg-red-500 text-white font-bold rounded-md transition-colors">Yes, Delete</button>
                </div>
            </div>
        </div>
    );
};


// --- SVG ICONS ---
const MenuIcon = ({ className = "w-6 h-6" }) => ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}> <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /> </svg> );
const CloseIcon = ({ className = "w-6 h-6" }) => ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}> <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /> </svg> );
const PlusIcon = ({ className = "w-6 h-6" }) => ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}> <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /> </svg> );
const SendIcon = ({ className = "w-6 h-6" }) => ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}> <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L6 12Zm0 0h7.5" /> </svg> );
const AshtavakraIcon = ({ className = "w-8 h-8" }) => ( <div className={`flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 ${className}`}> <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-white"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M9.5 15.5c-1.5 0-2.2-1-2.2-2s.7-2 2.2-2 2.2.9 2.2 2c0 1.2-.7 2-2.2 2z"/><path d="M14.5 15.5c1.5 0 2.2-1 2.2-2s-.7-2-2.2-2-2.2.9-2.2 2c0 1.2.7 2 2.2 2z"/><path d="M9.5 10.5c-1.5 0-2.2-1-2.2-2s.7-2 2.2-2 2.2 1 2.2 2c0 1-.7 2-2.2 2z"/><path d="M14.5 10.5c1.5 0 2.2-1 2.2-2s-.7-2-2.2-2-2.2 1-2.2 2c0 1 .7 2 2.2 2z"/></svg></div>);
const SignOutIcon = ({ className = "w-6 h-6" }) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}> <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /> </svg> );
const ProfileIcon = ({ className = "w-6 h-6" }) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}> <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /> </svg>);
const TrashIcon = ({ className = "w-6 h-6" }) => ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}> <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.134-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.067-2.09 1.02-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /> </svg> );

